import type { OwnerPanelPeriod } from '../domain/types';

export type { OwnerPanelPeriod };
export function todayRangeIso(now = new Date()): { from: string; to: string } {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(now);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function yesterdayRangeIso(now = new Date()): { from: string; to: string } {
  const from = new Date(now);
  from.setDate(from.getDate() - 1);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Últimos 7 días calendario (incluye hoy), fin del día actual. */
export function weekRangeIso(now = new Date()): { from: string; to: string } {
  const to = new Date(now);
  to.setHours(23, 59, 59, 999);
  const from = new Date(now);
  from.setDate(from.getDate() - 6);
  from.setHours(0, 0, 0, 0);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** 7 días previos al rango de semana actual. */
export function priorWeekRangeIso(now = new Date()): { from: string; to: string } {
  const { from: weekFrom } = weekRangeIso(now);
  const weekStart = new Date(weekFrom);
  const to = new Date(weekStart);
  to.setMilliseconds(-1);
  const from = new Date(to);
  from.setDate(from.getDate() - 6);
  from.setHours(0, 0, 0, 0);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Mes calendario en curso hasta ahora. */
export function monthRangeIso(now = new Date()): { from: string; to: string } {
  const from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const to = new Date(now);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Mes calendario anterior completo. */
export function priorMonthRangeIso(now = new Date()): { from: string; to: string } {
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function rangesForOwnerPanelPeriod(
  period: OwnerPanelPeriod,
  now = new Date(),
): { current: { from: string; to: string }; prior: { from: string; to: string }; priorLabel: string } {
  if (period === 'week') {
    return { current: weekRangeIso(now), prior: priorWeekRangeIso(now), priorLabel: 'vs sem. ant.' };
  }
  if (period === 'month') {
    return { current: monthRangeIso(now), prior: priorMonthRangeIso(now), priorLabel: 'vs mes ant.' };
  }
  return { current: todayRangeIso(now), prior: yesterdayRangeIso(now), priorLabel: 'vs ayer' };
}

export type SalesDeltaTone = 'up' | 'down' | 'flat' | 'new';

export type SalesDelta = {
  text: string;
  tone: SalesDeltaTone;
};

export function formatSalesDelta(current: number, prior: number): SalesDelta | null {
  if (prior <= 0) {
    if (current <= 0) return null;
    return { text: 'nuevo', tone: 'new' };
  }
  const pct = Math.round(((current - prior) / prior) * 100);
  if (pct === 0) return { text: '0%', tone: 'flat' };
  if (pct > 0) return { text: `↑ ${pct}%`, tone: 'up' };
  return { text: `↓ ${Math.abs(pct)}%`, tone: 'down' };
}

/** @deprecated Prefer `formatSalesDelta`. */
export function formatSalesDeltaPercent(today: number, yesterday: number): string | null {
  const d = formatSalesDelta(today, yesterday);
  if (!d) return null;
  if (d.tone === 'up') return `+${d.text.replace('↑ ', '')}`;
  if (d.tone === 'down') return `-${d.text.replace('↓ ', '')}`;
  return d.text;
}

export function formatRefreshedAgo(at: Date, now = new Date()): string {
  const mins = Math.max(0, Math.floor((now.getTime() - at.getTime()) / 60_000));
  if (mins < 1) return 'Actualizado recién';
  if (mins === 1) return 'Actualizado hace 1 min';
  return `Actualizado hace ${mins} min`;
}
